import type { TodayVisit } from "@visits/visitsClient";

/**
 * A room occupant row: one client's pets currently in the room. Usually a lone
 * patient (`visits.length === 1`), but a client with 2+ pets in the room right
 * now — a litter, or any household booked as several patients — collapses into a
 * single group so the tile stays scannable rather than stacking one row per pet
 * and growing without bound (WEB-3216). Opening the room still lists every pet.
 */
export interface RoomOccupantGroup {
  clientId: string | null;
  clientName: string | null;
  visits: TodayVisit[]; // always length >= 1
}

/**
 * Collapse a room's visits into occupant rows, grouping visits that share a `clientId`.
 *
 * Grouping keys on `clientId`, never `clientName`: the name is free text and two
 * unrelated households can share one, so the id is the only safe identity. A visit with
 * no `clientId` can't be proven to belong to any household, so it always renders alone.
 * Order follows the incoming today-list order, with a group anchored at its client's
 * first visit.
 */
export const groupRoomOccupants = (
  visits: TodayVisit[],
): RoomOccupantGroup[] => {
  const visitsByClient = new Map<string, TodayVisit[]>();
  for (const visit of visits) {
    if (!visit.clientId) continue;
    const bucket = visitsByClient.get(visit.clientId);
    if (bucket) bucket.push(visit);
    else visitsByClient.set(visit.clientId, [visit]);
  }

  const emittedClients = new Set<string>();
  const groups: RoomOccupantGroup[] = [];
  for (const visit of visits) {
    if (!visit.clientId) {
      // No id to group on — render this visit on its own.
      groups.push({
        clientId: null,
        clientName: visit.clientName,
        visits: [visit],
      });
      continue;
    }

    // Emit each client's whole household once, at its first pet's position.
    if (emittedClients.has(visit.clientId)) continue;
    emittedClients.add(visit.clientId);
    groups.push({
      clientId: visit.clientId,
      clientName: visit.clientName,
      visits: visitsByClient.get(visit.clientId)!,
    });
  }

  return groups;
};
